import * as Sentry from "@sentry/react";
import { AlertCircle } from "@untitledui/icons";
import { cx } from "class-variance-authority";
import type React from "react";
import { useEffect } from "react";
import { AlertBanner } from "@/components/alert-banner";
import { EmptyState } from "@/components/application/empty-state/empty-state";
import { Button } from "@/components/base/buttons/button";
import { type NavItem, SidebarLayout } from "@/components/sidebar/sidebar-layout";
import { useMerchantFeatures } from "@/hooks/use-merchant-features";
import { useIsMobile } from "@/hooks/use-mobile";
import { useRemoteConfig } from "@/hooks/use-remote-config";
import type { DashboardTab, PortfolioTab, WorkspacePortfolioTab, WorkspaceTab } from "@/lib/schema";
import { AdminNavbar } from "@/pages/admin/admin-navbar";
import { useAuth } from "@/providers/auth-provider";
import { useWorkspaceId } from "@/providers/MerchantProvider";

type LayoutProps = {
  activeTab: DashboardTab | PortfolioTab | WorkspaceTab | WorkspacePortfolioTab;
  sections: NavItem[];
} & React.PropsWithChildren;

const MobileBlocker = () => (
  <div
    className={cx(
      "self-stretch px-16 inline-flex flex-col justify-center items-center gap-16 overflow-hidden ",
      "bg-gradient-to-r from-[#ffd08a]/20 via-[#f9a3d0]/20 to-[#9fdcff]/20 brightness-110",
    )}
  >
    <div className="w-full flex flex-col justify-start items-start gap-4">
      <div className="self-stretch text-center justify-start text-[#181d27] text-2xl font-semibold font-['IBM_Plex_Sans'] leading-loose">
        This works best on a larger screen
      </div>
      <div className="self-stretch text-center justify-start text-[#535861] text-base font-normal font-['IBM_Plex_Sans'] leading-normal">
        Please expand your browser window or switch to a desktop/laptop for the full experience.
      </div>
    </div>
  </div>
);

export const Layout: React.FC<LayoutProps> = ({ activeTab, sections, children }) => {
  const isMobile = useIsMobile();
  const { user } = useAuth();
  const isAdminNavbarVisible = user?.role === "admin";
  const features = useMerchantFeatures();
  const showPastDueBanner = features.includes("pastDueBanner");
  const workspaceId = useWorkspaceId();
  const banner = useRemoteConfig("C-banner");
  const workspaceAlert = banner?.all ?? (workspaceId ? banner?.[workspaceId] : undefined) ?? null;

  useEffect(() => {
    if (isAdminNavbarVisible) {
      document.documentElement.style.setProperty("--sidebar-top-offset", "48px");
    } else {
      document.documentElement.style.removeProperty("--sidebar-top-offset");
    }
    return () => {
      document.documentElement.style.removeProperty("--sidebar-top-offset");
    };
  }, [isAdminNavbarVisible]);

  return (
    <>
      <AdminNavbar />
      <div
        className={cx("flex w-full", isAdminNavbarVisible ? "h-[calc(100vh-48px)]" : "min-h-screen")}
        style={isAdminNavbarVisible ? ({ "--sidebar-top-offset": "48px" } as React.CSSProperties) : undefined}
      >
        <SidebarLayout activeTab={activeTab} sections={sections} hasAdminBar={isAdminNavbarVisible} />
        {isMobile ? (
          <MobileBlocker />
        ) : (
          <div className={cx("flex-1 bg-white overflow-auto", "max-w-1600px", "min-w-1200px")}>
            {showPastDueBanner && (
              <AlertBanner>
                Your account has a past due invoice. Please make payment to avoid your account being paused.
              </AlertBanner>
            )}
            {workspaceAlert && (
              <AlertBanner>
                {workspaceAlert.title && <span className="font-semibold">{workspaceAlert.title} </span>}
                {workspaceAlert.message}
              </AlertBanner>
            )}
            <Sentry.ErrorBoundary
              onError={(e) => console.error(e)}
              fallback={
                <div className="h-[calc(100vh-4rem)] w-full flex items-center justify-center">
                  <EmptyState className="space-y-4" size="md">
                    <EmptyState.FeaturedIcon color="error" icon={AlertCircle} theme="light" />

                    <EmptyState.Content>
                      <EmptyState.Title>An unexpected error occured</EmptyState.Title>
                      <EmptyState.Description>
                        We've been alerted and are looking into the problem.
                      </EmptyState.Description>
                      <Button color="tertiary" onClick={() => window.location.reload()}>
                        Refresh
                      </Button>
                    </EmptyState.Content>
                  </EmptyState>
                </div>
              }
            >
              {children}
            </Sentry.ErrorBoundary>
          </div>
        )}
      </div>
    </>
  );
};
